import {
  LABELS,
  ROLE_CODES,
  USER_STATUSES,
  userCreateSchema,
  userResetPasswordSchema,
  userUpdateSchema,
} from '@mm/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Pencil, Plus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Card, StatusBadge } from '../../components/ui/display';
import { Modal } from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import { type FieldDef, ResourceForm } from '../../forms/ResourceForm';
import { DataTable } from '../../tables/DataTable';
import type { User } from '../../types/models';
import { formatDateTime } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';

const roleOptions = ROLE_CODES.map((r) => ({ value: r, label: LABELS.role[r] }));
const statusOptions = USER_STATUSES.map((s) => ({ value: s, label: LABELS.userStatus[s] }));

const createFields: FieldDef[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'email', label: 'Email', type: 'text', required: true },
  { name: 'roleCode', label: 'Role', type: 'select', required: true, options: roleOptions },
  {
    name: 'password',
    label: 'Initial password',
    type: 'password',
    required: true,
    help: 'At least 10 characters',
  },
];
const editFields: FieldDef[] = [
  { name: 'name', label: 'Name', type: 'text', required: true },
  { name: 'roleCode', label: 'Role', type: 'select', required: true, options: roleOptions },
  { name: 'status', label: 'Status', type: 'select', required: true, options: statusOptions },
];
const resetFields: FieldDef[] = [
  {
    name: 'password',
    label: 'New password',
    type: 'password',
    required: true,
    help: 'The user is signed out everywhere',
  },
];

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit'; user: User }
  | { kind: 'reset'; user: User }
  | null;

export function UsersPanel() {
  const { user: me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['users', page],
    queryFn: () => api.list<User>('/users', { page, pageSize: 25 }),
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] });
  const close = () => setDialog(null);

  return (
    <Card
      title="Users"
      description="Admin: full access · Accountant: financial data and GST · Viewer: read-only"
      bodyClassName="p-0"
      actions={
        <Button icon={<Plus className="size-4" />} onClick={() => setDialog({ kind: 'create' })}>
          Add user
        </Button>
      }
    >
      <DataTable<User>
        tableId="users"
        rows={query.data?.data}
        rowKey={(u) => u.id}
        loading={query.isLoading}
        meta={query.data?.meta}
        onPageChange={setPage}
        columns={[
          {
            key: 'name',
            header: 'User',
            render: (u) => (
              <div>
                <p className="font-medium text-slate-900">
                  {u.name}{' '}
                  {u.id === me?.id && (
                    <span className="text-xs font-normal text-slate-400">(you)</span>
                  )}
                </p>
                <p className="text-xs text-slate-500">{u.email}</p>
              </div>
            ),
          },
          { key: 'role', header: 'Role', render: (u) => <StatusBadge status={u.role.code} /> },
          { key: 'status', header: 'Status', render: (u) => <StatusBadge status={u.status} /> },
          {
            key: 'lastLoginAt',
            header: 'Last sign-in',
            render: (u) => formatDateTime(u.lastLoginAt),
          },
          {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            align: 'right',
            render: (u) => (
              <div className="flex justify-end gap-1">
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  onClick={() => setDialog({ kind: 'edit', user: u })}
                  aria-label="Edit user"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  type="button"
                  className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  onClick={() => setDialog({ kind: 'reset', user: u })}
                  aria-label="Reset password"
                >
                  <KeyRound className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
      />

      <Modal
        open={dialog !== null}
        onClose={close}
        size="sm"
        title={
          dialog?.kind === 'create'
            ? 'Add user'
            : dialog?.kind === 'edit'
              ? 'Edit user'
              : 'Reset password'
        }
        description={dialog && dialog.kind !== 'create' ? dialog.user.email : undefined}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" form="user-form">
              Save
            </Button>
          </>
        }
      >
        {dialog?.kind === 'create' && (
          <ResourceForm
            formId="user-form"
            fields={createFields}
            schema={userCreateSchema}
            defaultValues={{ name: '', email: '', roleCode: 'ACCOUNTANT', password: '' }}
            isEdit={false}
            lookups={undefined}
            onSubmit={async (v) => {
              await api.post('/users', v);
              toast.success('User created');
              close();
              void refresh();
            }}
          />
        )}
        {dialog?.kind === 'edit' && (
          <ResourceForm
            formId="user-form"
            fields={editFields}
            schema={userUpdateSchema}
            defaultValues={{
              name: dialog.user.name,
              roleCode: dialog.user.role.code,
              status: dialog.user.status,
            }}
            isEdit
            lookups={undefined}
            onSubmit={async (v) => {
              await api.put(`/users/${dialog.user.id}`, { ...v, updatedAt: dialog.user.updatedAt });
              toast.success('User updated');
              close();
              void refresh();
            }}
          />
        )}
        {dialog?.kind === 'reset' && (
          <ResourceForm
            formId="user-form"
            fields={resetFields}
            schema={userResetPasswordSchema}
            defaultValues={{ password: '' }}
            isEdit={false}
            lookups={undefined}
            onSubmit={async (v) => {
              await api.post(`/users/${dialog.user.id}/reset-password`, v);
              toast.success('Password reset');
              close();
            }}
          />
        )}
      </Modal>
    </Card>
  );
}
